import React from 'react';

/**
 * Returns `false` on first render, then flips to `true` once the browser is
 * idle after the initial paint. Use it to gate non-critical queries (e.g. nav
 * badge counts) so they don't compete with above-the-fold content for the
 * network and main thread during the initial load.
 *
 * Pass the result into a query's `enabled` flag alongside its real condition:
 *
 *   const deferred = useDeferAfterPaint();
 *   api.foo.bar.useQuery(input, { enabled: deferred && someCondition });
 */
export const useDeferAfterPaint = (): boolean => {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (typeof win.requestIdleCallback === 'function') {
      const handle = win.requestIdleCallback(() => setReady(true), { timeout: 2000 });
      return () => win.cancelIdleCallback?.(handle);
    }

    // Safari (no requestIdleCallback): fall back to a short timeout.
    const handle = window.setTimeout(() => setReady(true), 200);
    return () => window.clearTimeout(handle);
  }, []);

  return ready;
};
